import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  CalendarRange, Loader2, Pause, Play, Pencil, CalendarPlus, AlertTriangle, CalendarOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../../services/adminService';
import HoursPatternDialog from './HoursPatternDialog';
import HoursRunDialog from './HoursRunDialog';
import { WEEKDAY_LABELS, fmtDate, fmtWindow, hoursLabel, money } from '../../../lib/scheduleFormat';

const SERVES_TEXT = { same_day: 'same day', next_day: 'next day', both: 'same + next day' };

// The standing pattern lives next to the access agreement it depends on: it can
// only ever book inside what the landlord has already granted.
const HoursPatternCard = ({ kitchen }) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [run, setRun] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await adminService.getHoursPattern(kitchen.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the standing hours');
    } finally {
      setLoading(false);
    }
  }, [kitchen.id]);

  useEffect(() => { load(); }, [load]);

  const pattern = view?.pattern || null;
  const summary = view?.summary || {};

  const save = async (payload) => {
    setBusy(true);
    try {
      setView(await adminService.saveHoursPattern(kitchen.id, payload));
      setEditing(false);
      toast.success('Standing hours saved — use “Book hours ahead” to fill the calendar');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the standing hours');
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async () => {
    setBusy(true);
    try {
      const next = pattern?.status === 'active' ? 'paused' : 'active';
      setView(await adminService.setHoursPatternStatus(kitchen.id, next));
      toast.success(next === 'paused' ? 'Standing hours paused' : 'Standing hours active again');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change the status');
    } finally {
      setBusy(false);
    }
  };

  const openPreview = async () => {
    setBusy(true);
    try {
      setRun(await adminService.previewHoursPattern(kitchen.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not work out what to book');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const result = await adminService.applyHoursPattern(kitchen.id);
      setRun(result);
      toast.success(`${result.totals.bookings} day(s) booked · ${hoursLabel(result.totals.hours)}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not book those hours');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="hours-pattern-card">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
            <CalendarRange className="h-5 w-5 text-amber-600" /> Standing hours
            {pattern && (
              <Badge
                variant="outline"
                className={pattern.status === 'active'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-slate-300 bg-slate-100 text-slate-700'}
                data-testid="pattern-status"
              >
                {pattern.status === 'active' ? 'Active' : 'Paused'}
              </Badge>
            )}
          </CardTitle>
          <p className="text-sm text-gray-500">
            The usual hours, booked ahead so a normal week needs no booking pass.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {pattern && (
            <Button variant="ghost" size="sm" onClick={toggleStatus} disabled={busy}
              data-testid="pattern-toggle-status">
              {pattern.status === 'active'
                ? <><Pause className="mr-1 h-3.5 w-3.5" /> Pause</>
                : <><Play className="mr-1 h-3.5 w-3.5" /> Resume</>}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="pattern-edit">
            <Pencil className="mr-1 h-3.5 w-3.5" /> {pattern ? 'Edit' : 'Set hours'}
          </Button>
          {pattern && (
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={openPreview} disabled={busy}
              data-testid="pattern-book-ahead">
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CalendarPlus className="mr-1 h-3.5 w-3.5" />}
              Book hours ahead
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <Loader2 className="h-5 w-5 animate-spin text-amber-600" />}

        {!loading && !pattern && (
          <p className="text-sm text-gray-600" data-testid="pattern-empty">
            No standing hours yet. Set the usual window for each day and the next few weeks stay booked
            on their own — only the exceptions need you.
          </p>
        )}

        {!loading && pattern && (
          <>
            <div className="grid gap-1 text-xs text-gray-700 sm:grid-cols-2" data-testid="pattern-week">
              {pattern.days.map((day) => (
                <p key={day.weekday}>
                  <span className="inline-block w-9 font-medium text-gray-900">
                    {WEEKDAY_LABELS[day.weekday]}
                  </span>
                  {day.closed
                    ? <span className="text-gray-400">closed</span>
                    : <>{fmtWindow(day.start_time, day.end_time)}
                      <span className="ml-1 text-gray-500">· {SERVES_TEXT[day.serves] || 'next day'}</span></>}
                </p>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 text-xs" data-testid="pattern-summary">
              <Badge variant="outline" className="bg-white">
                {summary.booked_through
                  ? `Booked ahead to ${fmtDate(summary.booked_through)}`
                  : 'Nothing booked by the pattern yet'}
              </Badge>
              <Badge variant="outline" className="bg-white">
                Horizon {pattern.horizon_weeks} week{pattern.horizon_weeks === 1 ? '' : 's'} → {fmtDate(summary.horizon_end)}
              </Badge>
              <Badge variant="outline" className="bg-white">
                {hoursLabel(summary.hours_ahead)} · {money(summary.cost_ahead)} booked from today
              </Badge>
            </div>

            {summary.unbooked_count > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                data-testid="pattern-unbooked">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  {summary.unbooked_count} day{summary.unbooked_count === 1 ? '' : 's'} in the pattern have no hours booked
                </p>
                <p className="mt-1">
                  {summary.unbooked_dates.map((date) => fmtDate(date)).join(' · ')}
                  {summary.unbooked_count > summary.unbooked_dates.length ? ' …' : ''}
                </p>
                <p className="mt-1 text-amber-800">
                  “Book hours ahead” shows exactly what it would book before anything is committed.
                </p>
              </div>
            )}

            {summary.cancelled_dates?.length > 0 && (
              <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900"
                data-testid="pattern-cancelled">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <CalendarOff className="h-4 w-4" /> Cancelled by hand — left alone each night
                </p>
                <p className="mt-1">
                  {summary.cancelled_dates.map((row) => `${fmtDate(row.date)}${row.window ? ` (${row.window})` : ''}`).join(' · ')}
                </p>
                <p className="mt-1">A “Book hours ahead” run lists these and puts them back if you confirm it.</p>
              </div>
            )}

            {pattern.last_run && (
              <p className="text-xs text-gray-500" data-testid="pattern-last-run">
                Last run {pattern.last_run_kind === 'nightly' ? 'overnight' : 'by hand'}:
                {' '}{pattern.last_run.booked} booked, {pattern.last_run.trimmed} trimmed,
                {' '}{pattern.last_run.skipped} skipped — through {fmtDate(pattern.last_run.through)}.
              </p>
            )}
          </>
        )}
      </CardContent>

      {editing && (
        <HoursPatternDialog
          open kitchen={kitchen} pattern={pattern} busy={busy}
          onClose={() => setEditing(false)} onSubmit={save}
        />
      )}
      {run && (
        <HoursRunDialog
          open run={run} busy={busy}
          title={run.committed ? 'Hours booked' : 'Book the standing hours ahead'}
          description={run.committed
            ? 'What this run committed, per date.'
            : 'Nothing is booked until you confirm. Hours outside the access agreement are trimmed to fit, and anything that cannot be booked is listed with the reason.'}
          onConfirm={commit}
          onClose={() => setRun(null)}
        />
      )}
    </Card>
  );
};

export default HoursPatternCard;
